import React, { useState, useEffect } from 'react';
import { Mail, Send, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import { sendMeetingEmail } from '../../services/api';

const buildDefaultMessage = (name) =>
  `Dear ${name || 'team member'},\n\n` +
  'The HR department would like to schedule a meeting with you. ' +
  'Kindly make yourself available and reply to this email to confirm a suitable time.\n\n' +
  'Best regards,\nHR Department';

/**
 * Reusable "Invite staff to meeting" email composer.
 * Sends from the verified Vynce domain to the employee's own inbox.
 * HR can edit the subject/message at send time (keeps content open).
 */
const MeetingEmailDialog = ({ open, onOpenChange, employee }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setSubject(`Meeting Request from HR - ${employee.name || ''}`.trim());
      setMessage(buildDefaultMessage(employee.name));
    }
  }, [open, employee]);

  if (!employee) return null;

  const noEmail = !employee.email;

  const handleSend = async () => {
    if (noEmail) return;
    setSending(true);
    try {
      const res = await sendMeetingEmail(employee.id, { subject, message });
      toast({ title: 'Meeting email sent', description: res.data?.message || `Sent to ${employee.email}` });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Could not send email',
        description: err?.response?.data?.detail || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="meeting-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            Invite {employee.name} to a meeting
          </DialogTitle>
          <DialogDescription>
            {noEmail ? (
              <span className="text-rose-600">
                This employee has no email address on file. Add one in their profile first.
              </span>
            ) : (
              <>Sends to <span className="font-medium text-gray-700">{employee.email}</span> from Vynce HR. You can edit the wording below.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="meeting-subject">Subject</Label>
            <Input
              id="meeting-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={noEmail || sending}
              data-testid="meeting-email-subject"
            />
          </div>
          <div>
            <Label htmlFor="meeting-message">Message</Label>
            <Textarea
              id="meeting-message"
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={noEmail || sending}
              data-testid="meeting-email-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="meeting-email-cancel">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={noEmail || sending || !subject.trim() || !message.trim()} data-testid="meeting-email-send">
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" /> Send email</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MeetingEmailDialog;
